'use strict';

import React, {Component} from 'react';
import {observer} from "mobx-react";
import PropTypes from 'prop-types';
import classnames from 'classnames';

import {FaLink} from "react-icons/fa";

import Badge from "react-bootstrap/Badge";
import ListGroup from 'react-bootstrap/ListGroup';
import Form from 'react-bootstrap/Form';
import Tabs from 'react-bootstrap/Tabs';
import Tab from 'react-bootstrap/Tab';
import ButtonGroup from "react-bootstrap/ButtonGroup";

import SimpleState from '@uw-it-sis/lib-react/lib/SimpleState';
import {isEmpty, pluralize} from "@uw-it-sis/lib-react/lib/Utils";
import appStore from '@uw-it-sis/lib-react/lib/AppStore';

import AddOrRemoveSectionButton from './AddOrRemoveSectionButton';
import SectionCode from "./SectionCode";
import CourseDetailsStore from "./CourseDetailsStore";
import {normalize} from "./CourseUtils";
import {InstitutionCodes} from './SectionConstants';
import SectionMeetingDetails from './SectionMeetingDetails';

const analyticsStore = appStore.getAnalyticsStore();

function ActivityListItem(props) {
    const activity = props.activity;
    /**
     * Handler for "SPS" section status page click.
     */
    const onSectionStatusClick = (courseCode, sectionCode, sln, status) => {
        analyticsStore.addEvent("RegPrev-SectionStatusLinkClick", {},
            {cCode: courseCode, sCode: sectionCode, sln: sln, status: status});
    };

    let courseCode = normalize(activity.courseCode);

    // If summer term, create a badge for display
    let summerBadge = null;
    if ( activity.hasOwnProperty("summerTerm") && activity.summerTerm ) {
        summerBadge =
            <>
                <span className="sr-only">Summer Term: </span>
                <Badge bg="light-gray" className="text-dark align-middle fs-9 text-uppercase fw-normal border ms-2">{activity.summerTerm}</Badge>
            </>;
    }

    // If section is active, show current enrollment status/count/maximum, else show badge of section state (withdrawn/suspended)
    let enrollment;
    if ( activity.stateKey === 'active' && !isEmpty(activity.enrollStatus)) {
        let enrollStatus = null;
        let enrollStatusValue = activity.enrollStatus ? activity.enrollStatus.toLowerCase() : "";

        if ( enrollStatusValue === 'open' || enrollStatusValue === 'closed' ) {
            let enrollBadgeVariant;
            switch (enrollStatusValue) {
                case "closed":
                    enrollBadgeVariant = "danger";
                    break;
                case "open":
                    enrollBadgeVariant = "success";
                    break;
                default:
                    enrollBadgeVariant = "white";
            }

            enrollStatus =
                <Badge bg={enrollBadgeVariant} className="fs-9 text-uppercase">
                    {enrollStatusValue}
                </Badge>;
        }

        // Don't show negative numbers for available seats since sometimes counts are larger than maximums
        let availableSeats = Math.max(0, (activity.enrollMaximum - activity.enrollCount));

        enrollment =
            <div className="d-flex align-items-center">
                <span className="sr-only">Enrollment Status: </span>
                {enrollStatus}
                <Badge bg="light-gray" className="text-dark fs-9 text-uppercase fw-normal border">
                    <span>{availableSeats} </span>
                    <span className="sr-only">seats available </span>
                    <abbr title="available" aria-hidden={true}>avail</abbr>
                    <span> of {activity.enrollMaximum}{activity.enrollEstimate === 'estimate' ? <abbr title="estimated">E</abbr> : ''}</span>
                    <span className="sr-only"> seats</span>
                </Badge>
            </div>;
    } else {
        enrollment = null;
        if ( activity.stateKey !== 'active' ) {
            enrollment =
                <div className="d-flex align-items-center">
                    <span className="sr-only">Section is </span>
                    <a href="https://helpcenter.uw.edu/myplan-faq/#enrollmentrestrictions" target="_blank">
                        <Badge bg="dark-beige" className="fs-9 text-uppercase">
                            {activity.stateKey}
                        </Badge>
                    </a>
                </div>;
        }
    }

   //course mode
    let mode = activity.onlineLearningText ? activity.onlineLearningText : "In-person";

    let blockRegistrationRow;
    if ( !isEmpty(activity.blockRegistration) ) {
        let blockRegText;
        let blockCoursesList =
            <ul className="comma-separated">
                {
                    activity.blockRegistration.courses.filter(blockCourse => !blockCourse.parent).map((blockCourse, i) => {
                        return <li key={i}>{blockCourse.label}</li>;
                    })
                }
            </ul>;
        // Section is a block registration parent
        if (activity.blockRegistration.parent) {
            blockRegText =
                <>
                    <span className="me-2">Includes:</span>
                    {blockCoursesList}
                </>;
        } else {
            // Section is a block registration child
            blockRegText = "Some seats reserved for cluster registration.";
        }

        blockRegistrationRow =
            <div className="d-flex align-items-baseline mt-2 mt-sm-0">
                <FaLink aria-hidden="true" flip="horizontal" className="text-muted me-2" />
                {blockRegText}
            </div>
    }

    let activityItem =
        <div className="d-flex align-items-start">
            <div className="sectionCode text-nowrap" style={{width: "47.5px", minWidth: "47.5px"}}>
                <SectionCode section={activity} additionalAnalytics={props.additionalAnalytics}/>
            </div>
            <div className="d-flex flex-column flex-grow-1">
                <div className="d-sm-flex justify-content-sm-between align-items-sm-center">
                    <div className="d-flex align-items-center">
                        <span className="sr-only"><abbr title="Schedule Line Number">SLN</abbr>: </span>

                        <a onClick={(e) => onSectionStatusClick(courseCode, activity.code, activity.registrationCode, e)}
                           href={'https://sdb.admin.washington.edu/timeschd/uwnetid/sln.asp?QTRYR=' + activity.qtryr + '&SLN=' + activity.registrationCode}
                           className="align-middle me-2"
                           target="_blank">
                            {activity.registrationCode}
                        </a>
                        <span className="sr-only">Section Type: </span>
                        <Badge bg="light-gray" className="text-dark align-middle fs-9 text-uppercase fw-normal border">
                            {activity.activityOfferingType}
                        </Badge>
                        {summerBadge}
                    </div>
                    {enrollment}
                </div>
                <div>
                    {blockRegistrationRow}
                    <SectionMeetingDetails meetings={activity.meetingDetailsList} mode={mode} campus={activity.campus} />
                    <div>
                        Instructor: {isEmpty(activity.instructor) ? "--" : activity.instructor}
                    </div>
                </div>
            </div>
            <ButtonGroup className="ms-auto ps-3">
                <AddOrRemoveSectionButton courseCode={courseCode}
                                          section={activity}
                                          additionalAnalytics={props.additionalAnalytics} />
            </ButtonGroup>
        </div>;

    let openEnrollStatuses = [
        "open",
        "add code required",
        "faculty code required"
    ];

    let sectionClasses =
        classnames(
            {"bg-planned": activity.planItemId !== null},
            {"d-none": props.showOpenOnly && ( activity.stateKey !== "active" || !openEnrollStatuses.includes(activity.enrollStatus ? activity.enrollStatus.toLowerCase() : "") )}
        );

    return (
        <ListGroup.Item as="li" data-planned={activity.planItemId !== null} className={sectionClasses}>
            {activityItem}
        </ListGroup.Item>
    );
}

class InstitutionSectionList extends Component {
    constructor(props) {
        super(props);
        this.props = props;
        this.isShowOpenSelected = true;
        this.openEnrollStatuses = ["open",
            "add code required",
            "faculty code required"];


        // If no planned sections
        if ( this.props.institution.courseOfferingTermList[0].activityOfferingItemList.filter(activity => activity.planItemId != null).length === 0 ){
            // Are there any sections open for the checkbox filter? (Since default is to be filtered)
            this.isShowOpenSelected = (this.props.institution.courseOfferingTermList[0].activityOfferingItemList.filter(activity => (activity.stateKey === "active" && this.openEnrollStatuses.includes(activity.enrollStatus ? activity.enrollStatus.toLowerCase() : ""))).length > 0);
        } else {
            // Are there any that are withdrawn or suspended
            if (this.props.institution.courseOfferingTermList[0].activityOfferingItemList.filter(activity => (activity.planItemId != null && activity.stateKey !== "active")).length > 0){
                this.isShowOpenSelected = false;
            }
        }

        this.state = {
            showOpenOnly: this.isShowOpenSelected
        };

        this.onShowOpenOnlyChange = this.onShowOpenOnlyChange.bind(this);
    }

    onShowOpenOnlyChange = () => this.setState({showOpenOnly: !this.state.showOpenOnly});

    render() {
        let institution = this.props.institution;
        let activities = institution.courseOfferingTermList[0].activityOfferingItemList;
        let sectionCount = activities.length, openSectionCount = 0;

        let activityList = activities.map((activity, s) => {
            if (activity.stateKey === "active" && this.openEnrollStatuses.includes(activity.enrollStatus)) {
                openSectionCount++;
            }

            return <ActivityListItem key={s} activity={activity} showOpenOnly={this.state.showOpenOnly}
                                     openEnrollStatuses={this.openEnrollStatuses} additionalAnalytics={this.props.additionalAnalytics}/>
        });

        let showMessage;
        if (this.state.showOpenOnly) {
            showMessage = `Showing ${openSectionCount} open out of ${sectionCount} ${pluralize("section", sectionCount)}`;
        } else {
            showMessage = `Showing ${sectionCount} ${pluralize("section", sectionCount)}`;
        }

        return (
            <>
                <Form.Check id={"showOnly" + institution.code}
                            type="checkbox"
                            name="showOpenOnly"
                            className="mb-2">
                    <Form.Check.Input type="checkbox"
                                      name={"showOnly" + institution.code}
                                      checked={this.state.showOpenOnly}
                                      onChange={this.onShowOpenOnlyChange} />
                    <Form.Check.Label type="checkbox">Show open sections only</Form.Check.Label>
                </Form.Check>
                <strong className="d-block mb-3 lead fw-bold">
                    {showMessage}
                </strong>
                <ListGroup as="ul">
                    {activityList}
                </ListGroup>
            </>
        );
    }
}

function InstitutionDisplay(props) {
    let component;

    const institutions = props.institutions;

    // If there are multiple institutions in the data, show tabbed UI, if not, no tabbed UI
    if (institutions.length > 1) {
        let institutionTabs = institutions.map((institution, i) => {
            let institutionName = (institution.code === InstitutionCodes.PCE
                ? "Professional & Continuing Education (PCE)"
                : institution.name);
            return (
                <Tab eventKey={institution.code} title={institutionName} key={i}>
                    <InstitutionSectionList institution={institution} {...props} />
                </Tab>
            );
        });

        component =
            <Tabs id="institutions-sections" variant="pills" className="material-design" mountOnEnter>
                {institutionTabs}
            </Tabs>;
    } else {
        let institution = institutions[0];
        component = <InstitutionSectionList institution={institution} {...props} />;
    }

    return component;
}

class SectionSelector extends Component {

    constructor(props) {
        super(props);
        this.props = props;

        this.store = new CourseDetailsStore();
        this.buildDoneComponent = this.buildDoneComponent.bind(this);
    }

    componentDidMount() {
        // Load the course section data for given course
        this.store.mountCourseDetailsOnly(this.props.courseCode, this.props.courseId);
    }

    componentWillUnmount() {
        this.store.unmountCourseDetailsOnly();
    }

    buildDoneComponent() {
        // Get the sections for the course in a given term
        const courseOfferings = this.store.getCourseOfferings(this.props.termId);

        return (
            <InstitutionDisplay institutions={courseOfferings} {...this.props} />
        );
    }

    render() {
        // Use 'dots' animation for first time load of course section data
        let pendingComponent = "dots";
        // If data is already present, keeping showing sections list while add/remove sections action is taking place.
        if ( this.store.hasCourseData() ) {
            pendingComponent = this.buildDoneComponent;
        }
        return <SimpleState state={this.store.state} doneComponentBuilder={this.buildDoneComponent} pendingComponentBuilder={pendingComponent} name="SectionSelector"/>
    }
}

SectionSelector.propTypes = {
    courseCode: PropTypes.string.isRequired,
    courseId: PropTypes.string.isRequired,
    termId: PropTypes.string.isRequired,
    additionalAnalytics: PropTypes.object
};

export default observer(SectionSelector);